import { AgTechFormControlUpdate } from '@agtech/web/components/Forms/Controls/Base/AgTechFormControls';
import AgTechTitleDetailHeader from '@agtech/web/components/Headers/AgTechTitleDetailHeader';
import { SaleLotCategory } from '@sales/data/lots/categories/LotCategoryData';
import React from 'react'
import LotCategoryDialog from '../../dialogs/LotCategoryDialog';
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import { LotDataGetLotCategoriesQuery } from '@sales/data/lots/categories/LotCategoryQueries';
import AgTechFormLookup, { AgTechFormLookupProps } from '@agtech/web/components/Forms/Controls/Lookups/AgTechFormLookup';

export type LotCategoryDropdownProps<TFormEntity> = {
    entityType: TFormEntity,
    getSelectedLotCategoryId: (formEntity: TFormEntity) => number | undefined,
    onLotCategorySelected: AgTechFormControlUpdate<TFormEntity, SaleLotCategory | undefined>,
    caption?: string,
    width?: number,
    categories?: {
        filterCategories?: (category: SaleLotCategory) => boolean,
    }
}

export const useLotCategorySelectionProps = <TFormEntity, >(
    props: LotCategoryDropdownProps<TFormEntity>
): AgTechFormLookupProps<TFormEntity, SaleLotCategory> => {
    const lotCategoriesQuery = useAgTechQuery({
        query: LotDataGetLotCategoriesQuery,
        projection: data => Object.values(data)
    });

    return {
        entityType: props.entityType,
        control: {
            caption: props.caption ?? "Category",
            width: props.width
        },
        selection: {
            selectionControlTitle: 'Lot Categories',
            data: {
                itemQuery: lotCategoriesQuery,
                selectedItemId: props.getSelectedLotCategoryId(props.entityType)?.toString(),
                searchItems: (item, filter) => item.name.includes(filter),
                filterItems: props.categories?.filterCategories
            },
            items: {
                itemText: c => c.name,
                itemRendering: c => (
                    <AgTechTitleDetailHeader
                        title={c.name}
                        detail={c.description}
                        titleFontSize='font-size-small'
                        detailFontSize='font-size-very-small'
                        detailClasses='mt-1-half'
                    />
                ),
                itemHeight: 60,
                itemCreationConfig: {
                    creationButtonText: 'New Category',
                    creationButtonAction: e => {
                        return new Promise<SaleLotCategory>((res, fail) => {
                            e.dialogs.openDialog({
                                dialog: () => (
                                    <LotCategoryDialog onLotCategorySubmitted={async createdCategory => {
                                        res(createdCategory);
                                    }} />
                                ),
                            });
                        });
                    },
                    noRecordsMessage: 'No lot categories were found in the system',
                    noRecordsPrompt: 'To create one, please click the button below'
                }
            },
            popup: {
                height: 240,
                width: 360
            },
            behavior: {
                onItemSelected: async (entity, val) => {
                    return props.onLotCategorySelected(entity, val);
                }
            }
        }
    }
}

const LotCategoryLookup = <TFormEntity, >(props: LotCategoryDropdownProps<TFormEntity>) => {
    let lotCategorySelectionProps = useLotCategorySelectionProps(props);
    
    return (
        <AgTechFormLookup
            {...lotCategorySelectionProps}     
        />
    )
}

export default LotCategoryLookup;